// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/chat/ChatHeader.tsx
// 🔧 PURPOSE: La testata della chat ospite (mockup approvato, testata BLU):
//             "‹" da 44px che chiude la chat (scorre via a destra), lo
//             stemma di Nonna Vittoria, il titolo in Fraunces, lo stato della
//             reception (può andare a capo; offline dice che si aspetta la
//             rete) e il globo da 44px con la lingua della traduzione, che
//             apre il menu delle lingue.
//             Con la tastiera aperta è più bassa e lo stato sta su una riga.
// ============================================

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Globe } from 'lucide-react';
import { SheetTitle } from '../ui/sheet';
import { BackButton } from '../../pages/services/ServicePageLayout';
import { cn } from '../../lib/utils';
import { SUPPORTED_TRANSLATION_LANGS, TranslationLangCode } from '../../lib/translationService';

/** Lo stemma ritagliato dal logo (lo stesso della Home, HomeMasthead.EMBLEM_SRC). */
export const CHAT_EMBLEM_SRC = '/icons/logo-emblem.png';

/** Il blu della testata, in chiaro (blue-600, il blu delle bolle e dell'invio). */
export const CHAT_HEADER_COLOR = '#2563EB';
/** In scuro la testata scende a blue-800: la barra di stato la segue. */
export const CHAT_HEADER_DARK_COLOR = '#1E40AF';

/** Lo stemma nel cerchio avorio. */
export const ChatEmblem: React.FC<{ size?: number; className?: string }> = ({ size = 40, className }) => (
  <span
    aria-hidden="true"
    style={{ width: size, height: size }}
    className={cn('flex flex-shrink-0 items-center justify-center rounded-full bg-nva-bone', className)}
  >
    <img src={CHAT_EMBLEM_SRC} alt="" width={Math.round(size * 0.8)} height={Math.round(size * 0.8)} className="block" decoding="async" />
  </span>
);

interface ChatHeaderProps {
  onBack: () => void;
  /** Senza rete: "Offline · in attesa di rete" e il pallino grigio. */
  offline: boolean;
  /** Tastiera aperta: testata più bassa, stato su una riga. */
  compact: boolean;
  /**
   * La chat parte dal bordo alto dello schermo: si lascia lo spazio della barra
   * di stato (e i 10px dello stile nativo nell'app installata su iPhone).
   */
  safeTop: boolean;
  translationLang: TranslationLangCode | null;
  langMenuOpen: boolean;
  onToggleLangMenu: () => void;
  onPickLang: (code: TranslationLangCode | null) => void;
  /** Globo e menu: un tocco fuori da qui chiude il menu. */
  langMenuRef: React.Ref<HTMLDivElement>;
  /** Altezza massima del menu (con la tastiera aperta lo spazio è poco). */
  langMenuMaxHeight?: number;
}

const ChatHeader: React.FC<ChatHeaderProps> = ({
  onBack,
  offline,
  compact,
  safeTop,
  translationLang,
  langMenuOpen,
  onToggleLangMenu,
  onPickLang,
  langMenuRef,
  langMenuMaxHeight,
}) => {
  const { t } = useTranslation();
  const currentLangLabel = translationLang
    ? SUPPORTED_TRANSLATION_LANGS.find((l) => l.code === translationLang)?.label ?? translationLang.toUpperCase()
    : t('guestChat.noTranslation');
  const statusText = offline ? t('guestChat.offline.status') : t('guestChat.status');

  const langRow = (selected: boolean) => cn(
    'flex min-h-[44px] w-full items-center gap-2.5 px-3.5 text-left text-[15px] transition-colors',
    'focus:outline-none focus-visible:bg-blue-50 dark:focus-visible:bg-blue-900/30',
    selected
      ? 'bg-blue-50 font-bold text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
      : 'font-medium text-nva-espresso hover:bg-nva-bone dark:text-stone-100 dark:hover:bg-stone-800',
  );

  return (
    <header
      className={cn(
        'relative z-[3] flex flex-shrink-0 items-center gap-2.5 px-4',
        'bg-gradient-to-br from-blue-600 to-blue-700 shadow-[0_2px_12px_rgba(29,78,216,.22)] dark:from-blue-800 dark:to-blue-900 dark:shadow-none',
        compact ? 'pb-2' : 'pb-3',
        // Con la tastiera aperta (safeTop falso) niente zona sicura, ma i 10px
        // dello stile nativo sì (app installata su iPhone, lib/statusBar): la
        // fascia sfumata di iOS resta sul blu, i comandi sotto.
        safeTop
          ? (compact ? 'pt-[calc(var(--nva-screen-top,0px)+8px)]' : 'pt-[calc(var(--nva-screen-top,0px)+12px)]')
          : 'pt-[calc(var(--nva-top-gap,0px)+var(--nva-top-edge,0px)+8px)]',
      )}
    >
      <BackButton onClick={onBack} label={t('guestChat.closeChat')} className="focus-visible:ring-offset-blue-700" />
      <ChatEmblem />
      <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
        <SheetTitle className="m-0 truncate font-display text-[19px] font-bold leading-[1.1] text-white dark:text-white">
          Nonna Vittoria
        </SheetTitle>
        <p
          className={cn(
            'm-0 flex min-w-0 items-start gap-1.5 text-[12.5px] font-medium leading-[1.3] text-white',
            !compact && 'min-h-[32px]',
          )}
        >
          <span
            aria-hidden="true"
            className={cn(
              'mt-1 h-2 w-2 flex-shrink-0 rounded-full',
              offline ? 'bg-stone-300' : 'bg-green-400 shadow-[0_0_0_2px_rgba(255,255,255,.3)]',
            )}
          />
          <span className={cn('min-w-0', compact && 'truncate')}>{statusText}</span>
        </p>
      </div>

      {/* La lingua della traduzione: il globo con la sigla, il menu sotto. */}
      <div className="relative flex-shrink-0" ref={langMenuRef}>
        <button
          type="button"
          onClick={onToggleLangMenu}
          className="relative flex h-11 w-11 items-center justify-center rounded-full border-[1.5px] border-white/55 bg-white/10 text-white transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-blue-700"
          aria-label={`${t('guestChat.translateLanguage')}: ${currentLangLabel}`}
          title={`${t('guestChat.translateLanguage')}: ${currentLangLabel}`}
          aria-expanded={langMenuOpen}
        >
          <Globe className="h-[21px] w-[21px]" strokeWidth={2} aria-hidden="true" />
          {translationLang && (
            <span
              aria-hidden="true"
              className="absolute -bottom-[3px] -right-1 flex h-[18px] min-w-[21px] items-center justify-center rounded-md bg-white px-1 text-[10px] font-extrabold uppercase leading-none tracking-[0.04em] text-blue-700"
            >
              {translationLang}
            </span>
          )}
        </button>

        {langMenuOpen && (
          <div
            role="group"
            aria-label={t('guestChat.translateLanguage')}
            style={langMenuMaxHeight ? { maxHeight: langMenuMaxHeight } : undefined}
            className="absolute right-0 top-full z-50 mt-2 max-h-[65vh] w-[250px] max-w-[calc(100vw-24px)] overflow-y-auto rounded-[18px] border border-nva-edge bg-nva-card py-1.5 shadow-[0_18px_40px_rgba(42,38,32,.2)] dark:border-stone-700 dark:bg-stone-900"
          >
            <p className="m-0 px-3.5 pb-1.5 pt-2 text-[10.5px] font-extrabold uppercase tracking-[0.14em] text-nva-gold-text dark:text-nva-gold-dark">
              {t('guestChat.translateLanguage')}
            </p>
            <button type="button" onClick={() => onPickLang(null)} aria-pressed={!translationLang} className={langRow(!translationLang)}>
              <span aria-hidden="true" className="w-6 flex-shrink-0" />
              <span className="flex-1">{t('guestChat.noTranslation')}</span>
              {!translationLang && <Check className="h-[17px] w-[17px] flex-shrink-0 text-blue-700 dark:text-blue-300" strokeWidth={2.6} aria-hidden="true" />}
            </button>
            {SUPPORTED_TRANSLATION_LANGS.map((l) => {
              const selected = translationLang === l.code;
              return (
                <button type="button" key={l.code} onClick={() => onPickLang(l.code)} aria-pressed={selected} className={langRow(selected)}>
                  <span
                    aria-hidden="true"
                    className={cn(
                      'w-6 flex-shrink-0 text-[12px] font-extrabold uppercase tracking-[0.04em]',
                      selected ? 'text-blue-700 dark:text-blue-300' : 'text-nva-muted dark:text-stone-400',
                    )}
                  >
                    {l.code}
                  </span>
                  <span className="flex-1">{l.label}</span>
                  {selected && <Check className="h-[17px] w-[17px] flex-shrink-0 text-blue-700 dark:text-blue-300" strokeWidth={2.6} aria-hidden="true" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </header>
  );
};

export default ChatHeader;
